import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { campaignsApi, Teaser, TeaserList } from "../../../api/maintenance/campaigns";
import { errMsg } from "./campaignUtils";
import { dayTime, localInputToMs, localZone, parseTs, toIso } from "./campaignMedia";
import { TEASER_LIMIT, TeaserContext, presetOptions, teaserProblem } from "./campaignTeasers";

/** Every push before the start, in send order: the campaign's own teaser time,
 *  set by its field as before, and the extra ones added here. Titles are the
 *  server's, as they will be sent. A full row of the announcement card's grid. */

interface Props {
  campaignId: number | null;
  /** the stored start, naive UTC: what the server judges a teaser against */
  startsAt: string | null;
  /** draft or scheduled */
  editable: boolean;
  /** changes when the builder reloads the campaign */
  reloadKey?: string | null;
  setErr: (m: string) => void;
  setMsg: (m: string) => void;
}

const inputStyle: React.CSSProperties = { border: "1px solid #e6e8ee", borderRadius: 7, padding: "6px 8px", fontSize: 13 };

const field = (label: string, node: React.ReactNode, hint?: React.ReactNode) => (
  <label className="ma-select">{label}{node}{hint && <span className="ma-hint">{hint}</span>}</label>
);

const CampaignTeasers: React.FC<Props> = ({ campaignId, startsAt, editable, reloadKey, setErr, setMsg }) => {
  const [list, setList] = useState<TeaserList | null>(null);
  const [loadErr, setLoadErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [custom, setCustom] = useState("");   // datetime-local, in the viewer's time
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (cid: number) => {
    try {
      const r = await campaignsApi.teasers(cid);
      setList(r.data); setLoadErr("");
    } catch (e: any) { setLoadErr(errMsg(e, "Could not load the teasers.")); }
  }, []);

  useEffect(() => {
    if (campaignId == null) { setList(null); return; }
    load(campaignId);
  }, [campaignId, reloadKey, load]);

  const items = (list?.items || []).slice().sort((a, b) => parseTs(a.send_at) - parseTs(b.send_at));
  const limit = list?.limit ?? TEASER_LIMIT;
  const full = items.length >= limit;
  const ctx: TeaserContext | null = startsAt
    ? { startsAt, now: Date.now(), taken: items.map((t) => t.send_at), limit }
    : null;
  const options = ctx ? presetOptions(ctx) : [];
  const customAt = custom ? localInputToMs(custom) : NaN;
  const customProblem = ctx && custom ? teaserProblem(customAt, ctx) : null;

  const close = () => { setAdding(false); setCustom(""); };

  const add = async (at: number) => {
    if (campaignId == null) return;
    setErr(""); setMsg(""); setBusy(true);
    try {
      await campaignsApi.addTeaser(campaignId, toIso(at));
      close();
      setMsg("Teaser added.");
    } catch (e: any) { setErr(errMsg(e, "Could not add the teaser")); }
    finally { setBusy(false); await load(campaignId); }
  };

  const remove = async (t: Teaser) => {
    if (campaignId == null || t.id == null || !window.confirm(`Remove the teaser at ${dayTime(parseTs(t.send_at))}?`)) return;
    setErr(""); setMsg(""); setBusy(true);
    try {
      await campaignsApi.deleteTeaser(campaignId, t.id);
      setMsg("Teaser removed.");
    } catch (e: any) { setErr(errMsg(e, "Could not remove the teaser")); }
    finally { setBusy(false); await load(campaignId); }
  };

  const saved = campaignId != null;
  // The box border is the last row's line; a cell border on top of it reads as 2px.
  const end = (last: boolean): React.CSSProperties | undefined => (last ? { borderBottom: "none" } : undefined);

  return (
    <div style={{ gridColumn: "1 / -1", border: "1px solid #e6e8ee", borderRadius: 9, overflow: "hidden" }}>
      <div style={{ background: "#fafbfc", padding: "8px 12px", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #e6e8ee" }}>
        Teasers{saved && list ? ` · ${items.length} of ${limit}` : ""}
        <span className="ma-spacer" />
        {saved && editable && (
          <button className="ma-btn" disabled={busy || adding || full || !list || !ctx} onClick={() => setAdding(true)}>
            <Plus size={14} /> Add teaser
          </button>
        )}
      </div>

      {loadErr && <div className="ma-error" style={{ margin: "8px 12px" }}>{loadErr}</div>}

      {adding && editable && ctx && (
        <div className="ma-card-b" style={{ background: "#f7f8ff", borderBottom: "1px solid #e6e8ee", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, alignItems: "end" }}>
          {options.map((o) => (
            // a div, not a label: a click on the words must not press the button
            <div key={o.key} className="ma-select">{o.label}
              <button className="ma-btn" disabled={busy || !!o.problem} onClick={() => add(o.at)}>
                <Plus size={13} /> {dayTime(o.at)}
              </button>
              {o.problem && <span className="ma-hint">{o.problem}</span>}
            </div>
          ))}
          {field(`Custom (${localZone()})`,
            <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} style={inputStyle} />,
            customProblem)}
          <div style={{ display: "flex", gap: 6 }}>
            <button className="ma-btn primary" disabled={busy || !custom || !!customProblem} onClick={() => add(customAt)}>
              {busy ? "Adding…" : "Add"}
            </button>
            <button className="ma-btn" disabled={busy} onClick={close}>Cancel</button>
          </div>
        </div>
      )}

      <div className="ma-tablewrap"><table className="ma-table">
        <tbody>
          {!saved && <tr><td colSpan={3} className="ma-empty" style={end(true)}>Save the draft to add teasers</td></tr>}
          {saved && items.map((t, i) => {
            const last = i === items.length - 1;
            return (
              <tr key={t.id ?? "primary"}>
                <td style={end(last)}>{dayTime(parseTs(t.send_at))}</td>
                <td style={{ whiteSpace: "normal", width: "100%", ...end(last) }} title={t.body}>“{t.title}”</td>
                <td style={{ textAlign: "right", ...end(last) }}>
                  {t.sent_at ? <span className="ma-pill ok" title={`Sent ${dayTime(parseTs(t.sent_at))}`}>Sent</span>
                    : t.primary ? <span className="ma-hint">set above</span>
                    : editable && (
                      <button className="ma-btn" disabled={busy} title="Remove" onClick={() => remove(t)}><Trash2 size={13} /></button>
                    )}
                </td>
              </tr>
            );
          })}
          {saved && list && !items.length && <tr><td colSpan={3} className="ma-empty" style={end(true)}>No teasers yet</td></tr>}
        </tbody>
      </table></div>
    </div>
  );
};

export default CampaignTeasers;
